import { useRead, ReadState, isCollection } from "@/components/LoadingStates";
import { useEffect, useId, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UsersRound, Plus, LogIn, Loader2 } from "lucide-react";
import WorkspacePanel from "@/components/WorkspacePanel";
import RoleBadge, { ROLE_META } from "@/components/RoleBadge";
import { classNames } from "@/lib/format";

export default function Team() {
  const { user, refresh } = useAuth();
  const listRead = useRead("/workspaces", (d) => d && isCollection(d.workspaces) && d.workspaces.every((w) => typeof w.name === "string"));
  const list = listRead.data?.workspaces || [];
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  // Which request is in flight ("create" | "join"); both forms stay locked while either runs.
  const [pending, setPending] = useState(null);
  const busy = pending !== null;
  const nameId = useId();
  const codeId = useId();

  const load = () => listRead.reload();
  useEffect(() => { setSelected(listRead.data?.current_workspace_id || null); }, [listRead.key, listRead.data?.current_workspace_id]);

  const create = async (e) => {
    e.preventDefault();
    setPending("create");
    try {
      const { data } = await api.post("/workspaces", { name });
      toast.success(`Workspace "${data.name}" created — you're now working in it`);
      setName("");
      await refresh();
      await load();
      setSelected(data.id);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setPending(null);
    }
  };

  const join = async (e) => {
    e.preventDefault();
    setPending("join");
    try {
      const { data } = await api.post("/workspaces/join", { code });
      toast.success(`Joined ${data.name}`);
      setCode("");
      await refresh();
      await load();
      setSelected(data.workspace_id);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setPending(null);
    }
  };

  const activate = async (id) => {
    try {
      await api.post("/workspaces/switch", { workspace_id: id });
      await refresh();
      toast.success(id ? "Workspace activated" : "Back to personal space");
    } catch (error) { toast.error(formatError(error)); }
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      <div>
        <div className="flex items-center gap-2">
          <UsersRound className="h-6 w-6 text-primary" />
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Team Workspaces</h1>
        </div>
        <p className="text-muted-foreground text-sm mt-1">
          Share watchlists, saved videos and the hook swipe file. Switch spaces from the sidebar — each space has its own library.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="role-legend">
        {Object.entries(ROLE_META).map(([key, m]) => (
          <div key={key} className="flex items-start gap-2 rounded-lg border border-border px-3 py-2">
            <RoleBadge role={key} className="mt-0.5" />
            <span className="text-xs text-muted-foreground">{m.desc}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        <div className="space-y-4">
          <Card className="p-4 space-y-3" data-testid="workspace-list-card">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Your spaces</div>
            <button type="button" aria-current={!user?.workspace_id ? "true" : undefined} onClick={() => { setSelected(null); activate(null); }} className={classNames("w-full text-left rounded-md px-3 py-2 text-sm transition-colors", !user?.workspace_id ? "bg-primary/10 text-primary font-semibold" : "hover:bg-accent")} data-testid="workspace-personal">
              Personal space
            </button>
            <ReadState read={listRead} scope="team" empty={!list.length} title="Aucun workspace" description="Créez un workspace ou rejoignez une équipe avec un code." primaryCta={{ label: "Créer un workspace", onClick: () => document.querySelector('[data-testid="workspace-name-input"]')?.focus() }}>
            {list.map((w) => (
              <button key={w.id} type="button" aria-current={user?.workspace_id === w.id ? "true" : undefined} onClick={() => { setSelected(w.id); if (user?.workspace_id !== w.id) activate(w.id); }} className={classNames("w-full text-left rounded-md px-3 py-2 text-sm transition-colors flex items-center gap-2", user?.workspace_id === w.id ? "bg-primary/10 text-primary font-semibold" : "hover:bg-accent")} data-testid={`workspace-item-${w.id}`}>
                <span className="flex-1 truncate">{w.name}</span>
                <span className="text-[10px] text-muted-foreground">{w.member_count} member{w.member_count !== 1 ? "s" : ""}</span>
                <RoleBadge role={w.my_role} testid={`workspace-item-role-${w.id}`} />
              </button>
            ))}
            </ReadState>
          </Card>

          <Card className="p-4" data-testid="workspace-create-card">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Create a workspace</div>
            <form onSubmit={create} className="flex gap-2">
              <label htmlFor={nameId} className="sr-only">Workspace name</label>
              <Input id={nameId} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Growth Team" minLength={2} required data-testid="workspace-name-input" />
              <Button type="submit" disabled={busy || name.trim().length < 2} aria-label={pending === "create" ? "Creating workspace…" : "Create workspace"} title="Create workspace" data-testid="workspace-create-button">{pending === "create" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}</Button>
            </form>
          </Card>

          <Card className="p-4" data-testid="workspace-join-card">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">Join with a code</div>
            <form onSubmit={join} className="flex gap-2">
              <label htmlFor={codeId} className="sr-only">Invite code</label>
              <Input id={codeId} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="8-character code" className="font-mono uppercase" minLength={4} required data-testid="workspace-code-input" />
              <Button type="submit" variant="outline" disabled={busy || code.trim().length < 4} aria-label={pending === "join" ? "Joining workspace…" : "Join workspace"} title="Join workspace" data-testid="workspace-join-button">{pending === "join" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}</Button>
            </form>
          </Card>
        </div>

        {selected ? (
          <WorkspacePanel key={selected} id={selected} onChanged={async () => { await refresh(); await load(); }} onDeleted={async () => { setSelected(null); await refresh(); await load(); }} />
        ) : (
          <Card className="p-10 text-center text-muted-foreground" data-testid="workspace-empty">
            Vous êtes dans votre espace personnel. Créez un workspace ou rejoignez une équipe avec un code pour collaborer.
          </Card>
        )}
      </div>
    </div>
  );
}
